import { useMemo } from 'react';
import { FieldError } from 'react-hook-form';
import ReactSelect, { Props as ReactSelectProps, StylesConfig } from 'react-select';

type DefaultOption = { value: string; label: string };
function isDefaultOption(option: unknown): option is DefaultOption {
  return typeof (option as DefaultOption)?.value === 'string';
}

export type SelectProps<OptionType = DefaultOption, IsMulti extends boolean = false> = ReactSelectProps<
  OptionType,
  IsMulti
> & {
  emptyLabel?: string;
  highlightSelected?: boolean;
  menuZIndex?: number;
  error?: FieldError;
  size?: 'sm' | 'md' | 'lg';
  controlMinHeight?: string | number;
  isSolid?: boolean;
};

export const Select = <OptionType = DefaultOption, IsMulti extends boolean = false>({
  isMulti,
  options = [],
  emptyLabel,
  highlightSelected = false,
  error,
  size = 'md',
  className,
  controlMinHeight,
  isSolid,
  ...props
}: SelectProps<OptionType, IsMulti>) => {
  const totalOptions = useMemo(() => {
    return isMulti ? options : emptyLabel ? [{ value: '', label: emptyLabel } as OptionType, ...options] : options;
  }, [isMulti, options, emptyLabel]);

  const customStyles: StylesConfig<OptionType, IsMulti> = {
    menuPortal: (base) => ({
      ...base,
      zIndex: props.menuZIndex ?? 9999
    }),
    menu: (base) => {
      return {
        ...base,
        zIndex: props.menuZIndex ?? 9999,
        marginTop: '4px',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
      };
    },
    menuList: (base) => ({
      ...base,
      padding: '4px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#F5F8FA' : state.isFocused ? '#F5F8FA' : provided.backgroundColor,
      color: '#5E6278',
      fontSize: '1.1rem',
      lineHeight: '1.5rem',
      padding: '0.75rem',
      marginBottom: '4px',
      borderRadius: '0.375rem',
      '&:active': {
        backgroundColor: '#F5F8FA'
      }
    }),
    control: (provided, state) => {
      const selectedValue = state.getValue()[0];
      const getCurrentValue = (option: OptionType) => {
        if (isDefaultOption(option)) {
          return option.value;
        }
        return props.getOptionValue ? props.getOptionValue(option) : '';
      };
      const rawValue = selectedValue ? getCurrentValue(selectedValue) : '';
      const isNotEmptyValue = rawValue !== '';
      const isHighlightedNotEmpty = highlightSelected && isNotEmptyValue;
      let backgroundColor = isHighlightedNotEmpty || isSolid ? '#F5F8FA' : 'white';

      if (state.isDisabled) {
        backgroundColor = 'rgba(239, 239, 239, 0.3)';
      }

      const borderColor = isSolid ? 'var(--bs-gray-200)' : 'rgb(209, 213, 219)';

      return {
        ...provided,
        minHeight: controlMinHeight ?? 'unset',
        borderWidth: '0',
        borderRadius: '0.375rem',
        padding: size === 'md' ? '0.675rem 0' : '0.275rem 0',
        backgroundColor,
        width: '100%',
        textAlign: 'left',
        color: '#5E6278',
        fontSize: '1.1rem',
        lineHeight: '1.5rem',
        boxSizing: 'border-box',
        outline: 'none',
        boxShadow: `inset 0 0 0 1px ${error ? '#FF4D4F' : borderColor}`,
        '&:hover': {
          borderColor: 'transparent'
        }
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      padding: size === 'md' ? '0 1rem' : '0 0.75rem'
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      caretColor: props.isSearchable ? '#5E6278' : 'transparent'
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: '0',
      color: '#5E6278',
      fontSize: '1.1rem',
      fontWeight: 500,
      lineHeight: '1.5rem'
    })
  };

  return (
    <ReactSelect
      {...props}
      options={totalOptions}
      isMulti={isMulti}
      styles={customStyles}
      className={className}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      components={{
        IndicatorSeparator: () => null,
        ClearIndicator: (indicatorProps) => {
          const { innerProps, clearValue } = indicatorProps;
          const handleClear: React.PointerEventHandler<HTMLDivElement> = (e) => {
            e.preventDefault();
            e.stopPropagation();
            clearValue();
          };
          return (
            <div
              {...innerProps}
              onPointerDown={handleClear}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="d-inline-block p-1 pr-0"
            >
              <i className="bi bi-x-lg"></i>
            </div>
          );
        },
        DropdownIndicator: () => (
          <div className="d-inline-block p-1 pe-3">
            <i className="bi bi-chevron-down"></i>
          </div>
        )
      }}
    />
  );
};
